
// src/lib/check-ins/weeklyCheckIn.js

import { supabase } from "../../supabaseClient";

const SA_TIMEZONE = "Africa/Johannesburg";

/* =========================================================
   DATE HELPERS
========================================================= */

function datePartsInSA(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const get = (type) =>
    parts.find((part) => part.type === type)?.value;

  return `${get("year")}-${get("month")}-${get("day")}`;
}

function addDays(isoDate, days) {
  const date = new Date(`${isoDate}T12:00:00Z`);

  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
}

function dateAtSAStart(isoDate) {
  return new Date(`${isoDate}T00:00:00+02:00`);
}

/* =========================================================
   REPORTING WEEK
========================================================= */

// Automatically determine the current reporting week's
// Monday using the actual South African date.
export function getCurrentSAWeekStart(
  referenceDate = new Date()
) {
  const today = datePartsInSA(referenceDate);

  const day = new Date(
    `${today}T12:00:00Z`
  ).getUTCDay();

  const mondayOffset = (day + 6) % 7;

  return addDays(today, -mondayOffset);
}

// Always use the real date.
// No temporary Friday simulation.
export function getWeeklyCheckInReferenceDate() {
  return new Date();
}

export function getWeeklyCheckInDates(weekStart) {
  return {
    weekStart,

    weekEnd: addDays(weekStart, 6),

    // Friday of the reporting week.
    dueDate: addDays(weekStart, 4),

    // Following Wednesday.
    graceEnd: addDays(weekStart, 9),

    // Friday at midnight, South African time.
    opensAt: dateAtSAStart(
      addDays(weekStart, 4)
    ),

    // Following Thursday at midnight, South African time.
    // The entire Wednesday is available for submissions.
    cutoff: dateAtSAStart(
      addDays(weekStart, 10)
    ),
  };
}

/* =========================================================
   WEEKLY STATUS
========================================================= */

export function getWeeklyCheckInStatus(
  weekStart,
  submission = null,
  referenceDate = getWeeklyCheckInReferenceDate()
) {
  // Completed submissions remain completed.
  if (submission?.submitted_at) {
    return "completed";
  }

  const dates = getWeeklyCheckInDates(weekStart);

  if (referenceDate < dates.opensAt) {
    return "upcoming";
  }

  if (referenceDate >= dates.cutoff) {
    return "missed";
  }

  const today = datePartsInSA(referenceDate);

  if (today === dates.dueDate) {
    return "due";
  }

  return "overdue";
}

export function canEditWeeklyCheckIn(
  weekStart,
  referenceDate = getWeeklyCheckInReferenceDate()
) {
  const { opensAt, cutoff } =
    getWeeklyCheckInDates(weekStart);

  return (
    referenceDate >= opensAt &&
    referenceDate < cutoff
  );
}

/* =========================================================
   FINANCIAL CALCULATIONS
========================================================= */

// Individual week's ending cash balance.
export function calculateClosingCash({
  opening_cash_balance,
  cash_sales_received,
  cash_spent,
}) {
  return (
    Number(opening_cash_balance || 0) +
    Number(cash_sales_received || 0) -
    Number(cash_spent || 0)
  );
}

// Sum the ending balances of all completed weeks.
// Each reporting week contributes exactly once.
export function calculateCumulativeCashBalance(
  checkIns = []
) {
  const byWeek = new Map();

  for (const checkIn of checkIns) {
    if (
      !checkIn?.week_start ||
      !checkIn?.submitted_at
    ) {
      continue;
    }

    byWeek.set(
      checkIn.week_start,
      Number(checkIn.closing_cash_balance || 0)
    );
  }

  return Array.from(byWeek.values()).reduce(
    (total, amount) => total + amount,
    0
  );
}

// Preview the cumulative total for a reporting week.
// Replaces that week's amount if it already exists
// rather than adding the same week twice.
export function calculateCumulativeCashPreview(
  checkIns = [],
  weekStart,
  currentClosingCash
) {
  const otherWeeks = checkIns.filter(
    (row) =>
      row.week_start !== weekStart &&
      row.submitted_at
  );

  return (
    calculateCumulativeCashBalance(otherWeeks) +
    Number(currentClosingCash || 0)
  );
}

/* =========================================================
   DATE FORMATTING
========================================================= */

export function formatWeeklyCheckInDate(isoDate) {
  if (!isoDate) return "";

  return new Intl.DateTimeFormat("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: SA_TIMEZONE,
  }).format(
    new Date(`${isoDate}T12:00:00+02:00`)
  );
}

/* =========================================================
   SUPABASE READ HELPERS
========================================================= */

export async function getMyWeeklyFinancialCheckIns() {
  const { data, error } = await supabase.rpc(
    "get_my_weekly_financial_check_ins"
  );

  if (error) throw error;

  return Array.isArray(data) ? data : [];
}

export async function getMyLatestWeeklyFinancialCheckIn() {
  const { data, error } = await supabase.rpc(
    "get_my_latest_weekly_financial_check_in"
  );

  if (error) throw error;

  return data || null;
}

export async function getOrStartMyWeeklyCheckInTracking() {
  const { data, error } = await supabase.rpc(
    "get_or_start_my_weekly_check_in_tracking"
  );

  if (error) throw error;

  return data;
}

/* =========================================================
   CHECK-IN CONTEXT
========================================================= */

export async function getMyWeeklyCheckInContext(
  weekStart = getCurrentSAWeekStart()
) {
  const submissions =
    await getMyWeeklyFinancialCheckIns();

  // Latest reporting weeks first.
  const sorted = [...submissions].sort(
    (a, b) =>
      String(b.week_start).localeCompare(
        String(a.week_start)
      )
  );

  // Existing submission for the requested week.
  const existing =
    sorted.find(
      (row) => row.week_start === weekStart
    ) || null;

  // Find the latest completed earlier week.
  // If a learner missed a week, the previous
  // completed submission can still provide prefill.
  const previous =
    sorted.find(
      (row) =>
        row.week_start < weekStart &&
        row.submitted_at != null
    ) || null;

  return {
    existing,
    previous,
    submissions: sorted,

    status: getWeeklyCheckInStatus(
      weekStart,
      existing
    ),

    dates: getWeeklyCheckInDates(weekStart),

    cumulativeBalance:
      calculateCumulativeCashBalance(sorted),
  };
}

/* =========================================================
   PREFILL THE FIVE QUESTIONS
========================================================= */

export function getWeeklyCheckInInitialValues(
  existing = null,
  previous = null
) {
  const source = existing || previous;

  if (!source) {
    return {
      opening_cash_balance: "",
      total_sales: "",
      cash_sales_received: "",
      total_spent: "",
      cash_spent: "",
    };
  }

  // Keep the previous week's opening cash amount.
  // Do not replace it with the closing cash amount.
  // The learner can change all five values before
  // submitting the new reporting week.
  return {
    opening_cash_balance: String(
      source.opening_cash_balance ?? ""
    ),

    total_sales: String(
      source.total_sales ?? ""
    ),

    cash_sales_received: String(
      source.cash_sales_received ?? ""
    ),

    total_spent: String(
      source.total_spent ?? ""
    ),

    cash_spent: String(
      source.cash_spent ?? ""
    ),
  };
}

/* =========================================================
   SAVE WEEKLY CHECK-IN
========================================================= */

export async function saveMyWeeklyFinancialCheckIn(
  weekStart,
  values
) {
  const status = getWeeklyCheckInStatus(weekStart);

  if (
    status === "missed" ||
    status === "upcoming"
  ) {
    throw new Error(
      status === "upcoming"
        ? "This check-in becomes available on Friday."
        : "This reporting week's Wednesday deadline has passed."
    );
  }

  const fields = [
    "opening_cash_balance",
    "total_sales",
    "cash_sales_received",
    "total_spent",
    "cash_spent",
  ];

  for (const field of fields) {
    const value = String(
      values[field] ?? ""
    ).trim();

    if (
      !value ||
      !Number.isFinite(Number(value))
    ) {
      throw new Error(
        "Please enter valid amounts in all fields."
      );
    }
  }

  const opening = Number(
    values.opening_cash_balance
  );

  const totalSales = Number(
    values.total_sales
  );

  const cashReceived = Number(
    values.cash_sales_received
  );

  const totalSpent = Number(
    values.total_spent
  );

  const cashSpent = Number(
    values.cash_spent
  );

  if (
    totalSales < 0 ||
    cashReceived < 0 ||
    totalSpent < 0 ||
    cashSpent < 0
  ) {
    throw new Error(
      "Sales and spending cannot be negative."
    );
  }

  if (cashReceived > totalSales) {
    throw new Error(
      "Cash received cannot exceed total sales."
    );
  }

  if (cashSpent > totalSpent) {
    throw new Error(
      "Cash spending cannot exceed total spending."
    );
  }

  // Production-safe RPC:
  // Both gwintest and gwint25 use the normal
  // database-enforced Friday-to-Wednesday window.
  const { data, error } = await supabase.rpc(
    "save_my_weekly_financial_check_in",
    {
      p_week_start: weekStart,
      p_opening_cash_balance: opening,
      p_total_sales: totalSales,
      p_cash_sales_received: cashReceived,
      p_total_spent: totalSpent,
      p_cash_spent: cashSpent,
    }
  );

  if (error) throw error;

  return data;
}

/* =========================================================
   WEEKLY HISTORY
========================================================= */

// Build reporting weeks since tracking began.
// A week is considered missed only after
// its Wednesday submission deadline passes.
export function buildWeeklyCheckInHistory(
  submissions = [],
  startWeek,
  referenceDate = new Date()
) {
  const currentWeek =
    getCurrentSAWeekStart(referenceDate);

  const byWeek = new Map(
    submissions.map((row) => [
      row.week_start,
      row,
    ])
  );

  const result = [];

  if (
    !startWeek ||
    startWeek > currentWeek
  ) {
    return result;
  }

  for (
    let week = currentWeek;
    week >= startWeek;
    week = addDays(week, -7)
  ) {
    const submission =
      byWeek.get(week) || null;

    result.push({
      week_start: week,

      ...getWeeklyCheckInDates(week),

      status: getWeeklyCheckInStatus(
        week,
        submission,
        referenceDate
      ),

      submission,
    });
  }

  return result;
}

/* =========================================================
   SUMMARY FOR DASHBOARD
========================================================= */

export function getWeeklyCheckInSummary(
  submissions = [],
  startWeek,
  referenceDate = new Date()
) {
  const history =
    buildWeeklyCheckInHistory(
      submissions,
      startWeek,
      referenceDate
    );

  // Only count weeks whose Friday opening
  // time has already been reached.
  const eligibleWeeks = history.filter(
    (week) =>
      referenceDate >= week.opensAt
  );

  const completed = eligibleWeeks.filter(
    (week) =>
      week.status === "completed"
  ).length;

  const missed = eligibleWeeks.filter(
    (week) =>
      week.status === "missed"
  ).length;

  const total = eligibleWeeks.length;

  return {
    completed,
    missed,
    total,

    progress:
      total > 0
        ? Math.round(
            (completed / total) * 100
          )
        : 0,
  };
}
